import { CSSProperties } from '@material-ui/core/styles/withStyles'
import { useMemo } from 'react'
import {
    Column,
    ColumnInstance,
    Filters,
    IdType,
    SortingRule,
    TableOptions,
    TableState,
} from 'react-table'
import {
    ActionColumnProps,
    SpecTableProps,
    TableFetchDataProps,
    TableFetchNextDataProps,
} from '.'
import CellDefault from './CellDefault'
import { SpecTableTitleSettingsProps } from './SpecTableTitleSettings'
import FilterTextInput from './filters/FilterTextInput'

export interface AdditionalColumnProps {
    sticky?: 'left' | 'right'
    stickyRightMargin?: number
    stickyLeftMargin?: number
}

export interface UseSpecTableOptionProps<TData extends Object> {
    /***** REQUIRED PROPS *****/
    /**
     * The data that gets rendered
     */
    data: TData[]
    /**
     * The structure of the columns.  Anything set on columns will override the defaultColumn
     */
    columns: Column<TData>[]
    /**
     * Spectable must have a containing div to compute the max width of the table.
     * Pass the ref of the containing div to this prop.
     */
    divContainerRef: React.MutableRefObject<HTMLDivElement | undefined>
    /**
     * This is the event that gets triggered for new fetches.  Normally triggered by pageSize / pageIndex / sortBy / filters / and groupBy change.
     */
    fetchData: (props: TableFetchDataProps<TData>) => void
    /**
     * Loading state for fetch data.
     */
    fetchDataLoading: boolean

    /**
     * Total number of items.  This is not data.length but the total items in the database.
     */
    totalItems: number

    /***** OPTIONAL PROPS *****/
    /**
     * This is the action column found at the end of the column.  If undefined,
     * there will be no action column.  The accessor and cell are required properties.
     * cell.row.original returns the complete object for that row.
     * Sample:
     * accessor: 'projectId',
     * Cell: ({ cell }) => <IconButton onClick={() => console.log(cell.row.original)}>Action</>
     */
    actionColumnProps?: ActionColumnProps<TData>
    /**
     * Allows grouping of columns.  This will turn off any paging and infinite scroll
     * DEFAULT: false
     */
    allowGrouping?: boolean
    /**
     * Table body cell CSS override
     */
    bodyCellCssOverride?: CSSProperties
    /**
     * The order of the columns. This will default to how you ordered your columns
     */
    columnOrder?: IdType<TData>[]
    /**
     * This is a global column settings.  Any column specific properties will override this object.
     * Very useful for setting up the minWidth, width, etc.
     * This defaults to have these properties on each column:
     *  - minWidth: 220
     *  - width: 300
     *  - Filter: default Textfield box
     */
    defaultColumn?: Column<TData>
    /**
     * Sets the initial state of rows that should be expanded.
     */
    expanded?: Record<IdType<TData>, boolean>
    /**
     * Event that gets triggered when it needs the next batch of data
     */
    fetchNextData?: (props: TableFetchNextDataProps<TData>) => void
    /**
     * Loading state for getting more data.  This won't show the skeleton for previously loaded data. Default: false
     */
    fetchNextDataLoading?: boolean
    filters?: Filters<TData>
    /**
     * group by columns.  allowGrouping must be True.
     */
    groupBy?: IdType<TData>[]
    hasNextPage: boolean
    /**
     * Height of the table
     */
    height?: number | string
    /**
     * Columns to hide
     */
    hiddenColumns?: IdType<TData>[]
    /**
     * The table state.  This contains all the states that happens within the table such as sortBy, filters, selectedRowIds, to name a few.
     */
    onStateChange?: (state: TableState<TData>) => void
    /**
     * Row count on each data load.
     */
    pageSize?: number
    /**
     * Height of the skeleton per row when data is loading. Default: 50
     */
    skeletonItemHeight?: number
    /**
     * Shows checkboxes on the first column to select items.  Default: false
     * To get the selected rows, handle the onStateChange event and the name of the property
     * from the state is selectedRowIds.
     */
    showCheckboxColumn?: boolean
    /**
     * Define the sortBy of the columns
     */
    sortBy?: SortingRule<TData>[]
    /**
     * The title and settings row configuration.  If undefined, it hides the bar completely
     */
    specTableTitleSettings?: SpecTableTitleSettingsProps<TData>
    /**
     * Selected rows default
     */
    selectedRowIds?: Record<IdType<TData>, boolean>
    /*
     * No data component view
     */
    noDataComponent?: React.ReactNode
    /*
     * Flag for project home page
     */
    isProjectHome?: boolean
    /**
     * When true, the expanded state will automatically reset when data is changed
     */
    autoResetExpanded?: boolean

    showBottomLoadingIndicator?: boolean
}

const useSpecTableOptions = <TData extends Object>({
    actionColumnProps,
    allowGrouping = false,
    bodyCellCssOverride,
    data,
    columns,
    columnOrder = [],
    divContainerRef,
    defaultColumn,
    expanded = {} as any,
    fetchData,
    fetchDataLoading,
    fetchNextData,
    fetchNextDataLoading,
    filters = [],
    groupBy = [],
    hasNextPage,
    height = 'auto',
    hiddenColumns = [],
    onStateChange,
    pageSize,
    showCheckboxColumn = true,
    skeletonItemHeight,
    sortBy = [],
    specTableTitleSettings,
    totalItems,
    selectedRowIds = {} as any,
    noDataComponent,
    isProjectHome,
    autoResetExpanded,
    showBottomLoadingIndicator,
}: UseSpecTableOptionProps<TData>) => {
    const finalizedDefaultColumn = useMemo(
        () =>
            ({
                Filter: FilterTextInput,
                Cell: CellDefault,
                minWidth: 220,
                width: 300,
                ...defaultColumn,
            } as ColumnInstance<TData>),
        [defaultColumn]
    )

    let initialState: Partial<TableState<TData>> = {
        columnOrder,
        filters,
        groupBy,
        hiddenColumns,
        sortBy,
        pageSize,
        expanded,
        selectedRowIds,
    }

    const tableOptions: TableOptions<TData> = {
        data,
        columns,
        defaultColumn: finalizedDefaultColumn,
        initialState,
        manualSortBy: true,
        manualFilters: true,
        manualGroupBy: false,
        isMultiSortEvent: () => true,
        autoResetExpanded,
    }

    return {
        actionColumnProps,
        allowGrouping,
        bodyCellCssOverride,
        divContainerRef,
        fetchData,
        fetchDataLoading,
        fetchNextData,
        fetchNextDataLoading,
        hasNextPage,
        height,
        onStateChange,
        showCheckboxColumn,
        skeletonItemHeight,
        tableOptions,
        specTableTitleSettings,
        totalItems,
        noDataComponent,
        isProjectHome,
        showBottomLoadingIndicator,
    } as SpecTableProps<TData>
}

export default useSpecTableOptions
